<script setup lang="ts">
import { ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { numericAndFloat } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;

const { t } = useI18n();

const props = defineProps<{
	screenToEdit: ScreenDO | null;
}>();

const emit = defineEmits<{
	"update-list": [];
}>();

const isDialogVisible = defineModel<boolean>();

const localScreen = ref<Pick<
	ScreenDO,
	"is_hidden" | "is_mandatory_must" | "minimum_advertising_cost_ex_vat"
> | null>(null);

watch(
	() => props.screenToEdit,
	(screen) => {
		if (screen) {
			localScreen.value = {
				is_hidden: screen.is_hidden,
				is_mandatory_must: screen.is_mandatory_must,
				minimum_advertising_cost_ex_vat: screen.minimum_advertising_cost_ex_vat,
			};
		}
	},
	{ immediate: true }
);

const save = async () => {
	if (!props.screenToEdit || !localScreen.value) return;

	const response = await toastApiResult(
		useApiClient("campaignPlanner").updateScreen(
			props.screenToEdit.id,
			localScreen.value
		)
	);

	if (response.isOk()) {
		isDialogVisible.value = false;
		emit("update-list");
	}
};
</script>

<template>
	<v-dialog
		v-if="localScreen"
		v-model="isDialogVisible"
		max-width="600"
		persistent
	>
		<v-card>
			<v-toolbar color="lightprimary" elevation="0" flat>
				<template #prepend>
					<v-icon class="ml-3">fa: fa-regular fa-display</v-icon>
				</template>
				<v-toolbar-title>{{ props.screenToEdit?.name }}</v-toolbar-title>
				<template #append>
					<v-btn icon variant="text" @click="isDialogVisible = false">
						<v-icon>fa: fa-regular fa-xmark</v-icon>
					</v-btn>
				</template>
			</v-toolbar>

			<v-divider />

			<v-card-text class="pa-6">
				<v-row>
					<v-col cols="12">
						<div class="text-subtitle-1 font-weight-semibold mb-1">
							Minimum advertising cost (ex VAT)
						</div>
						<v-text-field
							v-model="localScreen.minimum_advertising_cost_ex_vat"
							prepend-inner-icon="fa: fa-regular fa-euro-sign"
							variant="filled"
							clearable
							hide-details="auto"
							:rules="[numericAndFloat(t)]"
						/>
					</v-col>

					<v-col cols="12">
						<v-divider class="my-2" />
					</v-col>

					<v-col cols="12">
						<div class="text-subtitle-1 font-weight-semibold mb-1">Hidden</div>
						<div class="text-body-2 text-medium-emphasis mb-2">
							Hidden screens are excluded from screen selection in flights.
						</div>
						<div class="d-flex align-center gap-2">
							<v-checkbox
								v-model="localScreen.is_hidden"
								hide-details
								color="primary"
							/>
							<span class="text-body-2">Mark as hidden</span>
						</div>
					</v-col>

					<v-col cols="12">
						<v-divider class="my-2" />
					</v-col>

					<v-col cols="12">
						<div class="text-subtitle-1 font-weight-semibold mb-1">
							Mandatory must screen
						</div>
						<div class="text-body-2 text-medium-emphasis mb-2">
							Drive sales on this screen by making it a mandatory 'must-screen'
							when available for screen selection.
						</div>
						<div class="d-flex align-center gap-2">
							<v-checkbox
								v-model="localScreen.is_mandatory_must"
								hide-details
								color="primary"
							/>
							<span class="text-body-2">Mark as mandatory must</span>
						</div>
					</v-col>
				</v-row>
			</v-card-text>

			<v-divider />

			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" @click="isDialogVisible = false">Cancel</v-btn>
				<v-btn variant="tonal" color="primary" @click="save">Save</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss"></style>
